import React, { useState } from 'react';
import { Archive, RotateCcw, AlertTriangle, Loader2 } from 'lucide-react';
import type { Filiere } from '@/types/filiere.types';

interface ArchiveConfirmModalProps {
  filiere: Filiere;
  action: 'archive' | 'restore';
  onConfirm: () => void;
  onCancel: () => void;
}

const ArchiveConfirmModal: React.FC<ArchiveConfirmModalProps> = ({
  filiere,
  action,
  onConfirm,
  onCancel,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    setIsLoading(true);
    await onConfirm();
    setIsLoading(false);
  };

  const isArchive = action === 'archive';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onCancel} />

        {/* Modal */}
        <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-200">
          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div
              className={`w-16 h-16 rounded-full flex items-center justify-center ${
                isArchive ? 'bg-amber-100' : 'bg-green-100'
              }`}
            >
              {isArchive ? (
                <Archive className="w-8 h-8 text-amber-600" />
              ) : (
                <RotateCcw className="w-8 h-8 text-green-600" />
              )}
            </div>
          </div>

          {/* Title */}
          <h2 className="text-xl font-semibold text-gray-900 text-center mb-2">
            {isArchive ? 'Archiver cette filière ?' : 'Restaurer cette filière ?'}
          </h2>

          {/* Filière Name */}
          <div className="text-center mb-4">
            <span className="inline-block px-3 py-1 bg-gray-100 rounded-full text-gray-700 font-medium">
              {filiere.filiere}
            </span>
            <span className="block text-sm text-gray-500 mt-1">
              {filiere.codeFiliere} • {filiere.diplome}
            </span>
          </div>

          {/* Warning Message */}
          {isArchive ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium mb-1">Cette action va :</p>
                  <ul className="space-y-1 text-amber-700">
                    <li>• Masquer la filière des formulaires de création d'étudiants</li>
                    <li>• Conserver les références pour les anciens étudiants</li>
                    <li>• Permettre une restauration à tout moment</li>
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <RotateCcw className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-green-800">
                  <p className="font-medium mb-1">Cette action va :</p>
                  <ul className="space-y-1 text-green-700">
                    <li>• Réactiver la filière</li>
                    <li>• La rendre disponible dans les formulaires</li>
                    <li>• Permettre l'inscription de nouveaux étudiants</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3">
            <button
              onClick={onCancel}
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50 font-medium"
            >
              Annuler
            </button>
            <button
              onClick={handleConfirm}
              disabled={isLoading}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-white rounded-xl transition-colors disabled:opacity-50 font-medium ${
                isArchive
                  ? 'bg-amber-600 hover:bg-amber-700'
                  : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {isArchive ? 'Archivage...' : 'Restauration...'}
                </>
              ) : (
                <>
                  {isArchive ? (
                    <>
                      <Archive className="w-4 h-4" />
                      Archiver
                    </>
                  ) : (
                    <>
                      <RotateCcw className="w-4 h-4" />
                      Restaurer
                    </>
                  )}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArchiveConfirmModal;
